import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppShell from '../../components/AppShell';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Stepper from '../../components/Stepper';
import { WIZARD_STEPS, WizardStatus } from '../../components/WizardShell';
import { api, ApiError, type ConceptsResponse } from '../../lib/api';
import { cx } from '../../lib/cx';

/** Phrased the way a customer says it, not the way a tag is named. */
const NUDGES: { key: string; label: string }[] = [
  { key: 'traditional', label: 'More traditional' },
  { key: 'contemporary', label: 'More contemporary' },
  { key: 'glass', label: 'More glass' },
  { key: 'taller', label: 'Taller' },
  { key: 'simpler', label: 'Simpler' },
  { key: 'warmer', label: 'Warmer materials' },
];

export default function Concepts() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<ConceptsResponse | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [large, setLarge] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setData(await api.getConcepts(id));
    } catch {
      setError('We could not load your concepts. Try again in a moment.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async (nudge?: string) => {
    if (!id) return;
    setGenerating(true);
    setError(null);
    setNote(null);
    try {
      const out = await api.renderConcepts(id, nudge);
      if (out.partial) {
        setNote('One of the three did not come back. The others are below.');
      }
      await load();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : 'The concepts did not come back. Nothing was counted against your limit.',
      );
    } finally {
      setGenerating(false);
    }
  };

  if (!data && !error) return <WizardStatus message="Loading your concepts…" />;

  const remaining = data ? data.batchesPerDay - data.batchesUsedToday : 0;
  const outOfBatches = remaining <= 0;

  return (
    <AppShell>
      <Stepper steps={WIZARD_STEPS} current={6} />

      <div className="mt-10">
        <p className="t-eyebrow text-green-600">Concepts</p>
        <h1 className="t-h1 mt-3 text-ink">Your building, roughly</h1>
        <p className="t-body-lg measure mt-4 text-grey-600">
          Three takes on the direction we read from your picks. They are illustrations of an
          idea, not a design — the point is to argue with them.
        </p>
      </div>

      {data && !data.generationEnabled && (
        <div className="mt-8">
          <Callout title="Concept generation is switched off">
            The pipeline is built and the rest of your project is saved. Generation stays off
            until it is switched on for this site, because every set of images bills the
            image service.
          </Callout>
        </div>
      )}

      {error && (
        <div className="mt-8">
          <Callout tone="caution" title="That did not work">
            {error}
          </Callout>
        </div>
      )}
      {note && (
        <div className="mt-8">
          <Callout title="Partly done">{note}</Callout>
        </div>
      )}

      {data && data.concepts.length === 0 && data.generationEnabled && (
        <div className="mt-10">
          <Button onClick={() => void generate()} disabled={generating || outOfBatches}>
            {generating ? 'Drawing… this takes about a minute' : 'Generate my concepts'}
          </Button>
        </div>
      )}

      {data && data.concepts.length > 0 && (
        <>
          <ul className="mt-10 grid gap-6 lg:grid-cols-3">
            {data.concepts.map((concept, i) => (
              <li
                key={concept.id}
                className="overflow-hidden rounded-lg border border-grey-200 bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setLarge(concept.url)}
                  className="block w-full"
                  aria-label={`See concept ${i + 1} larger`}
                >
                  <img
                    src={concept.url}
                    alt={`Concept ${i + 1}`}
                    className="block w-full bg-grey-100"
                    loading="lazy"
                  />
                </button>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="t-meta text-grey-600">Concept {i + 1}</span>
                  <a
                    href={concept.url}
                    download
                    className="t-meta min-h-tap inline-flex items-center font-semibold text-green-700 underline decoration-1 underline-offset-4"
                  >
                    Save
                  </a>
                </div>
              </li>
            ))}
          </ul>

          {data.generationEnabled && (
            <section className="mt-12">
              <h2 className="t-h2 text-ink">Not quite right?</h2>
              <p className="t-body measure mt-2 text-grey-600">
                Nudge it one way and we will draw another three.{' '}
                <span className="t-numeral">{remaining}</span> more{' '}
                {remaining === 1 ? 'set' : 'sets'} today.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {NUDGES.map((n) => (
                  <button
                    key={n.key}
                    type="button"
                    disabled={generating || outOfBatches}
                    onClick={() => void generate(n.key)}
                    className={cx(
                      'min-h-tap rounded-full border border-grey-300 bg-white px-5 text-[0.95rem] text-ink transition-colors',
                      'hover:border-green-600 hover:bg-green-050 disabled:cursor-not-allowed disabled:opacity-45',
                    )}
                  >
                    {n.label}
                  </button>
                ))}
              </div>
              {outOfBatches && (
                <div className="mt-6">
                  <Callout title="That is today's limit">
                    You have generated {data.batchesPerDay} sets today. More tomorrow, or
                    reply to your email and we will lift it.
                  </Callout>
                </div>
              )}
            </section>
          )}
        </>
      )}

      <div className="mt-12 flex flex-col-reverse gap-3 border-t border-grey-200 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="secondary" block onClick={() => navigate(`/project/${id}/results`)}>
          Back to my style
        </Button>
        <Button block onClick={() => navigate(`/project/${id}/committee`)}>
          Share with my committee
        </Button>
      </div>

      {large && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Enlarged concept"
          className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4 sm:p-8"
          onClick={() => setLarge(null)}
        >
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setLarge(null)}
              className="min-h-tap rounded-full border border-white/35 px-6 text-[0.95rem] font-semibold text-white hover:bg-white/10"
            >
              Close
            </button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center py-4">
            <img src={large} alt="" className="max-h-full max-w-full object-contain" />
          </div>
        </div>
      )}
    </AppShell>
  );
}
